import { z } from 'zod'
import { StaffRole } from './enums'
import { conditionalRequiredErrors, RequiredWhen } from './requiredWhen'

/** Custom-object field definitions (design 7.3). Shared by the API and the console. */
export const FieldType = z.enum([
  'text', 'long_text', 'number', 'money', 'boolean', 'date', 'datetime', 'email', 'phone', 'url', 'select', 'multi_select', 'reference',
])
export type FieldType = z.infer<typeof FieldType>

export const FieldDef = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/, 'lower-case letters, digits and underscores'),
  label: z.string().min(1),
  type: FieldType,
  required: z.boolean().default(false),
  /** Required only when another field has a value ("PO number when type is Corporate", F 15.3-03). */
  required_when: RequiredWhen.optional(),
  options: z.array(z.string()).optional(),          // select, multi_select
  reference: z.string().optional(),                 // object key for reference fields: person, organisation, <custom>
  indexed: z.boolean().default(false),
  pii: z.boolean().default(false),
  help: z.string().optional(),
  ai_writable: z.boolean().default(true),
  /** Staff roles that may see this field; absent means everyone who may read the object (design 7.3 "permissions"). */
  visible_to: z.array(StaffRole).optional(),
  /** Staff roles that may change this field; absent means everyone who may write the object. */
  editable_by: z.array(StaffRole).optional(),
  /** Where the field came from: the industry pack (a standard field, not tagged Custom) or the company. */
  origin: z.enum(['pack', 'company']).optional(),
  /**
   * A value worked out from linked records, never typed in (an order's line count, a fee per linked item): the
   * number of `object` records whose reference field `via` points at this record, optionally only those whose `where`
   * field holds one of the listed values, times `times_minor` when given (money). Kept up to date on every write to
   * the linked records (services/records.ts), so a stored count can never disagree with the records it counts.
   */
  rollup: z.object({
    object: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/),
    via: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/),
    where: z.object({ field: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/), in: z.array(z.union([z.string(), z.number(), z.boolean()])).min(1) }).optional(),
    times_minor: z.number().int().optional(),
  }).optional(),
})
export type FieldDef = z.infer<typeof FieldDef>

/** How an object's screens are laid out (design 7.3 "layouts the company can adjust"). Keys are field keys. */
export const ObjectLayout = z.object({
  /** Columns on the list screen, in order. */
  list: z.array(z.string()).max(12).optional(),
  /** Sections on the record screen, in order; fields not placed in a section are shown after them. */
  sections: z.array(z.object({ title: z.string().min(1).max(80), fields: z.array(z.string()) })).max(20).optional(),
})
export type ObjectLayout = z.infer<typeof ObjectLayout>

/** Which staff roles may read and write an object's records; absent means every role with the CRM space. */
export const ObjectPermissions = z.object({
  read: z.array(StaffRole).optional(),
  write: z.array(StaffRole).optional(),
})
export type ObjectPermissions = z.infer<typeof ObjectPermissions>

export const ObjectTypeInput = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/),
  label: z.string().min(1),
  plural: z.string().min(1),
  icon: z.string().optional(),
  fields: z.array(FieldDef),
  title_field: z.string().optional(),
  links: z.object({ person: z.boolean().default(true), organisation: z.boolean().default(true) }).default({ person: true, organisation: true }),
  ai_access: z.enum(['none', 'read', 'write']).default('read'),
  layout: ObjectLayout.optional(),
  permissions: ObjectPermissions.optional(),
})
export type ObjectTypeInput = z.infer<typeof ObjectTypeInput>

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

function fieldSchema(f: FieldDef): z.ZodType {
  switch (f.type) {
    case 'text': return z.string().max(2000)
    case 'long_text': return z.string().max(100_000)
    case 'number': return z.number().finite()
    case 'money': return z.number().int() // minor units
    case 'boolean': return z.boolean()
    case 'date': return z.string().regex(ISO_DATE, 'YYYY-MM-DD')
    case 'datetime': return z.iso.datetime({ offset: true })
    case 'email': return z.email()
    case 'phone': return z.string().regex(/^\+[1-9]\d{6,14}$/, 'E.164 phone number')
    case 'url': return z.url()
    case 'select': return z.enum((f.options?.length ? f.options : ['']) as [string, ...string[]])
    case 'multi_select': return z.array(z.enum((f.options?.length ? f.options : ['']) as [string, ...string[]]))
    case 'reference': return z.uuid()
  }
}

/**
 * Builds the validator for a record's data from its object's field list.
 * Unknown keys are rejected so a typo can never silently create a field.
 * `partial` validates an update that touches only some fields.
 */
export function recordSchema(fields: FieldDef[], opts: { partial?: boolean } = {}) {
  const shape: Record<string, z.ZodType> = {}
  for (const f of fields) {
    // A required field must hold a value: never null, and never an empty text or list. A partial update may leave it
    // out, but may not clear it.
    let s = fieldSchema(f)
    if (f.required && (f.type === 'text' || f.type === 'long_text')) s = (s as z.ZodString).trim().min(1, 'A value is needed')
    if (f.required && f.type === 'multi_select') s = (s as z.ZodArray<any>).min(1, 'Choose at least one')
    if (!f.required) s = s.nullable()
    if (opts.partial || !f.required) s = s.optional() as any
    shape[f.key] = s
  }
  return z.object(shape).strict()
}

export function validateRecord(fields: FieldDef[], data: unknown, opts: { partial?: boolean } = {}) {
  const r = recordSchema(fields, opts).safeParse(data)
  if (r.success) {
    // Conditional requirements apply to a whole record (a create, or an update merged onto the record).
    const missing = opts.partial ? [] : conditionalRequiredErrors(fields, r.data as Record<string, unknown>)
    if (missing.length) return { ok: false as const, errors: missing }
    return { ok: true as const, data: r.data as Record<string, unknown> }
  }
  return {
    ok: false as const,
    errors: r.error.issues.map((i) => ({ field: i.path.join('.') || '(record)', message: i.message })),
  }
}
